import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A command on the undo/redo stacks. `undo` reverts the action, `redo` re-applies
 * it. Both are expected to route through the normal optimistic + WS-synced board
 * mutations, so collaborators see undo/redo like any other edit. Per-board and
 * in-memory (cleared on board switch / reload) — the same model Monday & Linear
 * use for record edits.
 */
export interface UndoCommand {
  undo: () => void;
  redo: () => void;
}

const MAX_DEPTH = 100;

export function useUndoStack(resetKey: string | null) {
  const undoRef = useRef<UndoCommand[]>([]);
  const redoRef = useRef<UndoCommand[]>([]);
  const [, force] = useState(0);
  const bump = useCallback(() => force((n) => n + 1), []);

  // Reset both stacks when the board changes — undo history is per board.
  useEffect(() => {
    undoRef.current = [];
    redoRef.current = [];
    bump();
  }, [resetKey, bump]);

  const push = useCallback(
    (cmd: UndoCommand) => {
      undoRef.current.push(cmd);
      if (undoRef.current.length > MAX_DEPTH) undoRef.current.shift();
      redoRef.current = [];
      bump();
    },
    [bump],
  );

  const undo = useCallback(() => {
    const cmd = undoRef.current.pop();
    if (!cmd) return;
    cmd.undo();
    redoRef.current.push(cmd);
    bump();
  }, [bump]);

  const redo = useCallback(() => {
    const cmd = redoRef.current.pop();
    if (!cmd) return;
    cmd.redo();
    undoRef.current.push(cmd);
    bump();
  }, [bump]);

  return {
    push,
    undo,
    redo,
    canUndo: undoRef.current.length > 0,
    canRedo: redoRef.current.length > 0,
  };
}
